/**
 * Syncs delivery-delay shipping option radios / PDP variant changes to line item properties.
 * Initial PDP copy is rendered by Liquid; JS only updates on variant change.
 * Requires window.DeliveryDelay from delivery-delay.js (load that script first).
 */
(function () {
  const OPTIONS_SELECTOR = "[data-delivery-delay-options]";
  const PROPERTY_INPUT_SELECTOR = "[data-delivery-delay-property]";
  const STATIC_PROPERTY_SELECTOR = "input[data-delivery-delay-static-property]";
  const PDP_NOTICE_SELECTOR = "[data-delivery-delay-pdp-notice]";
  const SHIPPING_DURATION_SELECTOR = "[data-delivery-delay-shipping-duration]";

  /**
   * @returns {typeof window.DeliveryDelay | null}
   */
  function dd() {
    return window.DeliveryDelay || null;
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} vars
   * @returns {string}
   */
  function interpolate(template, vars) {
    const api = dd();
    if (api && typeof api.interpolate === "function") {
      return api.interpolate(template, vars);
    }
    return String(template || "");
  }

  /**
   * @param {string} isoDate
   * @param {string} format
   * @returns {string}
   */
  function formatDate(isoDate, format) {
    const api = dd();
    if (api && typeof api.formatDate === "function") {
      return api.formatDate(isoDate, format);
    }
    return String(isoDate || "");
  }

  /**
   * @param {{ shipFrom?: string, reason?: string, name?: string, mode?: string } | null | undefined} data
   */
  function resolveItem(data) {
    const api = dd();
    if (api && typeof api.resolveItem === "function") {
      return api.resolveItem(data);
    }
    return data && data.shipFrom ? data : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLFormElement | null}
   */
  function findTargetForm(root) {
    const formId = root.getAttribute("data-delivery-delay-form-id");
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
    }

    const host = root.closest(".product-form, custom-bundle-builder-v2, .section-custom-bundle-builder-v2-root");
    if (host) {
      const nested = host.querySelector('form[is="product-form"]');
      if (nested instanceof HTMLFormElement) return nested;
    }

    const sectionRoot = root.closest(".shopify-section");
    const sectionForm = sectionRoot?.querySelector('form[is="product-form"]');
    return sectionForm instanceof HTMLFormElement ? sectionForm : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {"primary" | "alternative"}
   */
  function getDefaultChoice(root) {
    const raw = (root.getAttribute("data-delivery-delay-default-choice") || "primary").trim();
    return raw === "alternative" ? "alternative" : "primary";
  }

  /**
   * @param {HTMLElement} root
   * @returns {{ name: string, value: string } | null}
   */
  function getSelectedProperty(root) {
    const primaryName = (root.getAttribute("data-delivery-delay-primary-name") || "").trim();
    const primaryValue = (root.getAttribute("data-delivery-delay-primary-value") || "").trim();
    const altValue = (root.getAttribute("data-delivery-delay-alt-value") || "").trim();

    const selected = root.querySelector('input[type="radio"]:checked');
    const choice = selected instanceof HTMLInputElement ? selected.value : getDefaultChoice(root);

    if (choice === "alternative" && altValue && primaryName) {
      return { name: primaryName, value: altValue };
    }

    if (primaryName && primaryValue) {
      return { name: primaryName, value: primaryValue };
    }

    return null;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ name: string, value: string } | null} property
   */
  function removeStaticPropertyInputs(form, property) {
    const staticInputs = form.querySelectorAll(STATIC_PROPERTY_SELECTOR);
    for (const input of staticInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (property && input.name === `properties[${property.name}]`) {
        input.remove();
        continue;
      }
      if (!property) {
        input.remove();
      }
    }

    // Clear SSR order-attribute companions only when wiping all static delay fields
    // (variant became non-delayed). Avoids leftover attributes[] on the form.
    if (!property) {
      form.querySelector("[data-delivery-delay-order-attribute]")?.remove();
      form.querySelector("[data-delivery-delay-order-items-attribute]")?.remove();
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ name: string, value: string } | null} property
   * @param {{ useOrderAttributes?: boolean, items?: string }} [options]
   */
  function syncPropertyInput(form, property, options = {}) {
    const existing = form.querySelector(PROPERTY_INPUT_SELECTOR);
    const useOrderAttributes = options.useOrderAttributes === true;
    const DD = dd();

    if (!property) {
      existing?.remove();
      removeStaticPropertyInputs(form, null);
      if (useOrderAttributes && DD && typeof DD.syncBundleOrderAttributes === "function") {
        DD.syncBundleOrderAttributes(form, null);
      }
      return;
    }

    removeStaticPropertyInputs(form, property);

    let input = existing;
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement("input");
      input.type = "hidden";
      input.setAttribute("data-delivery-delay-property", "");
      form.appendChild(input);
    }

    input.name = `properties[${property.name}]`;
    input.value = property.value;

    if (useOrderAttributes && DD && typeof DD.syncBundleOrderAttributes === "function") {
      DD.syncBundleOrderAttributes(form, {
        name: property.name,
        value: property.value,
        items: options.items || "",
      });
    }
  }

  /**
   * @param {HTMLElement} notice
   * @returns {boolean}
   */
  function noticeUsesOrderAttributes(notice) {
    return notice.getAttribute("data-delivery-delay-order-attributes") === "true";
  }

  /**
   * @param {HTMLElement} notice
   * @returns {string}
   */
  function readDelayedItemsLabel(notice) {
    const group = readBundleGroup(notice);
    if (group && Array.isArray(group.names) && group.names.length) {
      const api = dd();
      if (api && typeof api.formatItemsAttribute === "function") {
        return api.formatItemsAttribute(group.names);
      }
      return group.names.map((n) => String(n || "").trim()).filter(Boolean).join(", ");
    }
    return "";
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  function isCbbBundleForm(form) {
    return typeof form.id === "string" && form.id.startsWith("product-form-cbb-");
  }

  /**
   * @param {HTMLElement} root
   */
  function initDeliveryDelayNoticeOptions(root) {
    if (!(root instanceof HTMLElement) || !root.matches(OPTIONS_SELECTOR)) return;

    const form = findTargetForm(root);
    if (!(form instanceof HTMLFormElement)) return;

    // Bundle builder syncs the property via section-custom-bundle-builder-v2.js.
    if (isCbbBundleForm(form)) return;

    const property = getSelectedProperty(root);
    const notice = root.closest("[data-delivery-delay-pdp-notice]");
    const useOrderAttributes = notice instanceof HTMLElement && noticeUsesOrderAttributes(notice);
    syncPropertyInput(form, property, {
      useOrderAttributes,
      items: notice instanceof HTMLElement ? readDelayedItemsLabel(notice) : "",
    });
  }

  /**
   * @param {HTMLElement | null | undefined} host
   * @returns {Record<string, { shipFrom?: string, reason?: string, name?: string, mode?: string }>}
   */
  function readVariantMapFromHost(host) {
    if (!(host instanceof HTMLElement)) return {};
    const script = host.querySelector("[data-delivery-delay-variant-map]");
    if (!(script instanceof HTMLScriptElement)) return {};
    try {
      const parsed = JSON.parse(script.textContent || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_e) {
      return {};
    }
  }

  /**
   * @param {HTMLElement | null | undefined} host
   * @returns {{ state?: string, shipFrom?: string, names?: string[], count?: number, total?: number } | null}
   */
  function readBundleGroupFromHost(host) {
    if (!(host instanceof HTMLElement)) return null;
    const script = host.querySelector("[data-delivery-delay-bundle-group]");
    if (!(script instanceof HTMLScriptElement)) return null;
    try {
      const parsed = JSON.parse(script.textContent || "null");
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_e) {
      return null;
    }
  }

  /**
   * @param {HTMLElement} el
   * @param {string | number | null | undefined} variantId
   * @param {{ rewriteCopy?: boolean }} [options]
   */
  function updateShippingDurationBullet(el, variantId, options = {}) {
    if (!(el instanceof HTMLElement)) return;

    const rewriteCopy = options.rewriteCopy !== false;
    const fallback = (el.getAttribute("data-delivery-delay-shipping-fallback") || "").trim();
    const context = el.getAttribute("data-delivery-delay-context") || "single";
    const dateFormatLong = el.getAttribute("data-delivery-delay-date-format-long") || "%B %-d, %Y";
    const singleTemplate = el.getAttribute("data-delivery-delay-shipping-single") || "";
    const partialTemplate = el.getAttribute("data-delivery-delay-shipping-partial") || "";
    const host = el.closest(".shopify-section") || el.parentElement;

    let text = fallback;

    if (context === "bundle") {
      const group = readBundleGroupFromHost(host);
      if (!group?.shipFrom) return;
      const dateLong = formatDate(String(group.shipFrom), dateFormatLong);
      if (group.state === "PARTIAL") {
        text = interpolate(partialTemplate, { date: dateLong });
      } else {
        text = interpolate(singleTemplate, { date: dateLong });
      }
    } else if (context === "single") {
      const map = readVariantMapFromHost(host);
      const key = variantId != null ? String(variantId) : "";
      const delayData = key && map[key] ? map[key] : null;
      const resolved = resolveItem(delayData);

      if (resolved) {
        const dateLong = formatDate(resolved.shipFrom, dateFormatLong);
        text = interpolate(singleTemplate, { date: dateLong });
      }
    }

    if (rewriteCopy) {
      el.textContent = text;
    }
  }

  /**
   * @param {string | number | null | undefined} variantId
   * @param {{ rewriteCopy?: boolean, form?: HTMLFormElement | null }} [options]
   */
  function updateAllShippingDurationBullets(variantId, options = {}) {
    const rewriteCopy = options.rewriteCopy !== false;
    const form = options.form || null;

    for (const el of document.querySelectorAll(SHIPPING_DURATION_SELECTOR)) {
      if (!(el instanceof HTMLElement)) continue;

      if (form) {
        const section = el.closest(".shopify-section");
        const sectionForm = section?.querySelector("form[is='product-form'], form.product-form, form#product-form-main");
        if (sectionForm instanceof HTMLFormElement && sectionForm !== form) continue;
      }

      updateShippingDurationBullet(el, variantId, { rewriteCopy });
    }
  }

  /**
   * @param {HTMLElement} notice
   * @returns {Record<string, { shipFrom?: string, reason?: string, name?: string, mode?: string }>}
   */
  function readVariantMap(notice) {
    const host = notice.closest(".shopify-section") || notice.parentElement;
    return readVariantMapFromHost(host);
  }

  /**
   * @param {HTMLElement} notice
   * @returns {{
   *   context?: string,
   *   title: string,
   *   body: string,
   *   lineValue: string,
   *   partialTitleOne?: string,
   *   partialTitleOther?: string,
   *   partialBodyOne?: string,
   *   partialBodyOther?: string,
   *   fullTitle?: string,
   *   fullBody?: string,
   *   optionPartial?: string,
   *   optionPartialProperty?: string,
   *   optionFull?: string,
   *   recommended?: string
   * }}
   */
  function readCopyTemplates(notice) {
    const host = notice.closest(".shopify-section") || notice.parentElement;
    const script = host?.querySelector("[data-delivery-delay-copy-templates]");
    if (script instanceof HTMLScriptElement) {
      try {
        const parsed = JSON.parse(script.textContent || "{}");
        if (parsed && typeof parsed === "object") {
          return {
            context: String(parsed.context || "single"),
            title: String(parsed.title || ""),
            body: String(parsed.body || ""),
            lineValue: String(parsed.lineValue || ""),
            partialTitleOne: String(parsed.partialTitleOne || ""),
            partialTitleOther: String(parsed.partialTitleOther || ""),
            partialBodyOne: String(parsed.partialBodyOne || ""),
            partialBodyOther: String(parsed.partialBodyOther || ""),
            fullTitle: String(parsed.fullTitle || ""),
            fullBody: String(parsed.fullBody || ""),
            optionPartial: String(parsed.optionPartial || ""),
            optionPartialProperty: String(parsed.optionPartialProperty || ""),
            optionFull: String(parsed.optionFull || ""),
            recommended: String(parsed.recommended || ""),
          };
        }
      } catch (_e) {
        // fall through
      }
    }

    return {
      context: notice.getAttribute("data-delivery-delay-context") || "single",
      title: notice.getAttribute("data-delivery-delay-title-template") || "",
      body: notice.getAttribute("data-delivery-delay-body-template") || "",
      lineValue: notice.getAttribute("data-delivery-delay-line-value-template") || "",
    };
  }

  /**
   * @param {HTMLElement} notice
   * @returns {{ state?: string, shipFrom?: string, names?: string[], count?: number, total?: number } | null}
   */
  function readBundleGroup(notice) {
    const host = notice.closest(".shopify-section") || notice.parentElement;
    return readBundleGroupFromHost(host);
  }

  /**
   * @param {HTMLElement} notice
   * @param {string | number | null | undefined} variantId
   * @param {{ rewriteCopy?: boolean }} [options]
   */
  function updatePdpNoticeForVariant(notice, variantId, options = {}) {
    if (!(notice instanceof HTMLElement)) return;

    const rewriteCopy = options.rewriteCopy !== false;
    const context = notice.getAttribute("data-delivery-delay-context") || "single";
    const templates = readCopyTemplates(notice);
    const titleEl = notice.querySelector("[data-delivery-delay-notice-title]");
    const textEl = notice.querySelector("[data-delivery-delay-notice-text]");
    const optionsRoot = notice.querySelector(OPTIONS_SELECTOR);
    const propertyName = (notice.getAttribute("data-delivery-delay-property-name") || "").trim();
    const dateFormatLong = notice.getAttribute("data-delivery-delay-date-format-long") || "%B %-d, %Y";
    const listAnd = notice.getAttribute("data-delivery-delay-list-and") || "and";
    const key = variantId != null ? String(variantId) : "";
    const useOrderAttributes = noticeUsesOrderAttributes(notice);
    const orderAttrOpts = {
      useOrderAttributes,
      items: useOrderAttributes ? readDelayedItemsLabel(notice) : "",
    };

    let titleText = "";
    let bodyText = "";
    let lineValue = "";
    let shipFrom = "";

    if (context === "bundle" || templates.context === "bundle") {
      const group = readBundleGroup(notice);
      if (!group || !group.shipFrom || group.state === "NONE") {
        notice.hidden = true;
        if (optionsRoot instanceof HTMLElement) optionsRoot.hidden = true;
        const form = findTargetForm(notice);
        if (form instanceof HTMLFormElement && !isCbbBundleForm(form)) {
          syncPropertyInput(form, null, orderAttrOpts);
        }
        return;
      }

      shipFrom = String(group.shipFrom);
      const dateLong = formatDate(shipFrom, dateFormatLong);
      const count = Number(group.count) || 0;
      const names =
        window.DeliveryDelay && typeof window.DeliveryDelay.formatNames === "function"
          ? window.DeliveryDelay.formatNames(group.names || [], listAnd)
          : (group.names || []).join(", ");

      if (group.state === "FULL") {
        titleText = interpolate(templates.fullTitle || "", { date: dateLong, names, count });
        bodyText = interpolate(templates.fullBody || "", { date: dateLong, names, count });
      } else {
        titleText = interpolate(count === 1 ? templates.partialTitleOne || "" : templates.partialTitleOther || "", {
          date: dateLong,
          names,
          count,
        });
        bodyText = interpolate(count === 1 ? templates.partialBodyOne || "" : templates.partialBodyOther || "", {
          date: dateLong,
          names,
          count,
        });
      }
      lineValue = interpolate(templates.lineValue || "", { date: dateLong });
      if (useOrderAttributes) {
        orderAttrOpts.items =
          window.DeliveryDelay && typeof window.DeliveryDelay.formatItemsAttribute === "function"
            ? window.DeliveryDelay.formatItemsAttribute(group.names || [])
            : (group.names || []).filter(Boolean).join(", ");
      }
    } else {
      const map = readVariantMap(notice);
      const delayData = key && map[key] ? map[key] : null;
      const resolved = resolveItem(delayData);

      if (!resolved) {
        notice.hidden = true;
        if (optionsRoot instanceof HTMLElement) optionsRoot.hidden = true;

        const form = findTargetForm(notice);
        if (form instanceof HTMLFormElement && !isCbbBundleForm(form)) {
          syncPropertyInput(form, null, orderAttrOpts);
        }
        return;
      }

      shipFrom = resolved.shipFrom;
      const dateLong = formatDate(shipFrom, dateFormatLong);
      const names = resolved.name || "";
      titleText = interpolate(templates.title, { date: dateLong, names });
      bodyText = interpolate(templates.body, { date: dateLong, names });
      lineValue = interpolate(templates.lineValue, { date: dateLong });
      if (useOrderAttributes) orderAttrOpts.items = names;
    }

    const dateLongForOptions = formatDate(shipFrom, dateFormatLong);

    notice.hidden = false;
    notice.setAttribute("data-delivery-delay-current-variant-id", key);
    if (titleText) notice.setAttribute("aria-label", titleText);

    if (rewriteCopy) {
      if (titleEl instanceof HTMLElement) {
        titleEl.textContent = titleText;
        titleEl.hidden = titleText === "";
      }
      if (textEl instanceof HTMLElement) {
        textEl.textContent = bodyText;
        textEl.hidden = bodyText === "";
      }
    }

    if (optionsRoot instanceof HTMLElement) {
      optionsRoot.hidden = false;

      const optionPartialProperty = interpolate(
        templates.optionPartialProperty || templates.optionPartial || "",
        { date: dateLongForOptions },
      );
      optionsRoot.setAttribute(
        "data-delivery-delay-primary-value",
        optionPartialProperty || lineValue,
      );
      if (lineValue) {
        optionsRoot.setAttribute("data-delivery-delay-alt-value", lineValue);
      }

      const option1Label = optionsRoot.querySelector("[data-delivery-delay-option-1-label]");
      if (option1Label instanceof HTMLElement && templates.optionPartial) {
        option1Label.textContent = String(templates.optionPartial);
      }

      const option2Label = optionsRoot.querySelector("[data-delivery-delay-option-2-label]");
      if (option2Label instanceof HTMLElement) {
        const optionFullLabel = interpolate(templates.optionFull || "", { date: dateLongForOptions });
        if (optionFullLabel) option2Label.textContent = optionFullLabel;
      }

      initDeliveryDelayNoticeOptions(optionsRoot);
    } else {
      const form = findTargetForm(notice);
      if (form instanceof HTMLFormElement && !isCbbBundleForm(form) && propertyName && lineValue) {
        syncPropertyInput(form, { name: propertyName, value: lineValue }, orderAttrOpts);
      }
    }
  }

  function initAllPdpNotices() {
    for (const notice of document.querySelectorAll(PDP_NOTICE_SELECTOR)) {
      if (!(notice instanceof HTMLElement)) continue;

      // Keep Liquid-rendered copy on first paint; only sync cart property / options.
      const variantId = notice.getAttribute("data-delivery-delay-current-variant-id");
      updatePdpNoticeForVariant(notice, variantId, { rewriteCopy: false });
    }
  }

  function getDeliveryDelayThemeConfig() {
    const dd = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.deliveryDelay;
    const cfg = dd && typeof dd === "object" ? dd : {};
    return {
      quickViewNotice: cfg.quickViewNotice !== false,
      quickViewShippingOptions: cfg.quickViewShippingOptions === true,
      quickViewShowRecommended: cfg.quickViewShowRecommended !== false,
      quickViewRecommendedChoice: cfg.quickViewRecommendedChoice === "second" ? "alternative" : "primary",
      recommendedLabel: String((cfg.i18n && cfg.i18n.recommended) || "").trim(),
    };
  }

  function applyQuickViewShippingChoice(optionsRoot, cfg) {
    if (!(optionsRoot instanceof HTMLElement) || !cfg) return;

    optionsRoot.setAttribute("data-delivery-delay-default-choice", cfg.quickViewRecommendedChoice);

    const options = optionsRoot.querySelectorAll(".delivery-delay-notice__option");
    let badge = optionsRoot.querySelector(".delivery-delay-notice__option-badge");
    const recommendedLabel = (badge && badge.textContent ? badge.textContent.trim() : "") || cfg.recommendedLabel;

    for (const option of options) {
      const radio = option.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) continue;
      radio.checked = radio.value === cfg.quickViewRecommendedChoice;
    }

    if (!cfg.quickViewShowRecommended) {
      if (badge) badge.remove();
      return;
    }

    const target = [...options].find((option) => {
      const radio = option.querySelector('input[type="radio"]');
      return radio instanceof HTMLInputElement && radio.value === cfg.quickViewRecommendedChoice;
    });
    const textWrap = target && target.querySelector(".delivery-delay-notice__option-text");
    if (!(textWrap instanceof HTMLElement) || !recommendedLabel) return;

    if (!(badge instanceof HTMLElement)) {
      badge = document.createElement("span");
      badge.className = "delivery-delay-notice__option-badge";
      badge.textContent = recommendedLabel;
    }

    textWrap.appendChild(badge);
  }

  /**
   * Apply theme Quick view delayed-shipping settings after HTML inject.
   * Classic drawer/popover is also gated in Liquid; this covers full product modal.
   * @param {ParentNode | null | undefined} host
   */
  function applyQuickViewDeliveryDelaySettings(host) {
    if (!(host instanceof HTMLElement)) return;

    const cfg = getDeliveryDelayThemeConfig();
    if (!cfg.quickViewNotice) {
      for (const notice of host.querySelectorAll(PDP_NOTICE_SELECTOR)) {
        notice.remove();
      }
      return;
    }

    for (const notice of host.querySelectorAll(PDP_NOTICE_SELECTOR)) {
      if (!(notice instanceof HTMLElement)) continue;

      const optionsRoot = notice.querySelector(OPTIONS_SELECTOR);
      if (optionsRoot instanceof HTMLElement && !cfg.quickViewShippingOptions) {
        optionsRoot.remove();
      } else if (optionsRoot instanceof HTMLElement) {
        applyQuickViewShippingChoice(optionsRoot, cfg);
      }

      const variantId = notice.getAttribute("data-delivery-delay-current-variant-id");
      updatePdpNoticeForVariant(notice, variantId, { rewriteCopy: false });
    }
  }

  function initAll() {
    for (const root of document.querySelectorAll(OPTIONS_SELECTOR)) {
      if (root instanceof HTMLElement) initDeliveryDelayNoticeOptions(root);
    }
    initAllPdpNotices();
  }

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== "radio") return;

    const root = target.closest(OPTIONS_SELECTOR);
    if (!(root instanceof HTMLElement)) return;

    initDeliveryDelayNoticeOptions(root);
  });

  document.addEventListener("Variant:Changed", (event) => {
    const detail = event?.detail || {};
    const variant = detail.variant || detail;
    const variantId = variant?.id;
    if (variantId == null) return;

    const form = event.target instanceof HTMLFormElement ? event.target : null;
    for (const notice of document.querySelectorAll(PDP_NOTICE_SELECTOR)) {
      if (!(notice instanceof HTMLElement)) continue;
      if (form) {
        const noticeForm = findTargetForm(notice);
        if (noticeForm && noticeForm !== form) continue;
      }
      updatePdpNoticeForVariant(notice, variantId, { rewriteCopy: true });
    }
    updateAllShippingDurationBullets(variantId, { rewriteCopy: true, form });
  });

  // Prefer DOMContentLoaded so earlier deferred siblings (delivery-delay.js) have run.
  // During deferred execution readyState is often "interactive" but DOMContentLoaded has not fired yet.
  if (document.readyState === "complete") {
    initAll();
  } else {
    document.addEventListener("DOMContentLoaded", initAll);
  }

  window.initDeliveryDelayNoticeOptions = initDeliveryDelayNoticeOptions;
  window.updateDeliveryDelayPdpNotice = updatePdpNoticeForVariant;
  window.updateDeliveryDelayShippingDuration = updateAllShippingDurationBullets;
  window.applyQuickViewDeliveryDelaySettings = applyQuickViewDeliveryDelaySettings;
})();